import React, { useContext, useEffect, useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@material-ui/core';

import { CycleCountApi } from 'api/cycleCount';
import { UserContext } from 'contexts/UserContext';
import { DataTable, Notifications } from 'shared';

import { SkuCycleCountExpansion } from './skuCycleCountExpansion';
import { SkuCycleCountForm } from './skuCycleCountForm';
import { SourceSelector } from './SourceSelector';

const cycleCountApi = new CycleCountApi();

function SkuCycleCountTab() {
    const { user } = useContext(UserContext);
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: '', level: '' });
    const [selectedSources, setSelectedSources] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [requestIdToCancel, setRequestIdToCancel] = useState(null);

    const getSkuCycleCountRows = async () => {
        setIsLoading(true);
        const res = await cycleCountApi.getSkuCycleCountResults(user.facilityID, selectedSources);
        if (!res.status) {
            setNotification({ level: 'error', message: res.msg });
            return;
        }

        setRows(res.data.results);
        setIsLoading(false);
    };

    useEffect(() => {
        getSkuCycleCountRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSources]);

    const columns = [
        { Header: 'Cycle Count ID', accessor: 'inventoryCycleCountRequestID' },
        { Header: 'Date Requested', accessor: 'dateRequested' },
        { Header: 'Date Started', accessor: 'dateStarted' },
        { Header: 'Date Ended', accessor: 'dateEnded' },
        { Header: 'Manager', accessor: 'authUser' },
        { Header: 'Status', accessor: 'status' },
        { Header: 'Completed By', accessor: 'completedByAuthUsers' },
        { Header: 'Sku', accessor: 'sku' },
        { Header: 'Num Locations', accessor: 'numLocations' },
        { Header: 'Units Expected', accessor: 'unitsExpected' },
        { Header: 'Units Counted', accessor: 'unitsCounted' },
        { Header: 'Unit Accuracy', accessor: 'unitAccuracy' },
        {
            Header: 'Cancel',
            Cell: ({ row }) => {
                const cancellableStatuses = ['NEW', 'IN PROGRESS'];
                if (!cancellableStatuses.includes(row.original.status)) {
                    return null;
                }
                return (
                    <Button onClick={() => handleOpenCancelModal(row.original.inventoryCycleCountRequestID)}>
                        Cancel
                    </Button>
                );
            },
        },
    ];

    const handleOpenCancelModal = (requestId) => {
        setRequestIdToCancel(requestId);
        setCancelModalOpen(true);
    };

    const handleCloseCancelModal = () => {
        setCancelModalOpen(false);
        setRequestIdToCancel(null);
    };

    const handleCancel = async () => {
        const res = await cycleCountApi.cancelCycleCountRequest(requestIdToCancel);
        if (!res.status) {
            setNotification({ text: res.msg, level: 'error' });
            setCancelModalOpen(false);
            setRequestIdToCancel(null);
            return;
        }
        setNotification({ text: 'Cycle count request cancelled', level: 'success' });
        setCancelModalOpen(false);
        setRequestIdToCancel(null);
        getSkuCycleCountRows();
    };

    const handleCreateSuccess = () => {
        setNotification({ text: 'Cycle count request created', level: 'success' });
        setModalOpen(false);
        getSkuCycleCountRows();
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const toolbarActions = [
        {
            name: 'Create New Sku Cycle Count',
            action: () => {
                setModalOpen(true);
            },
        },
    ];

    return (
        <>
            <Notifications options={notification} />
            <div className="relative z-10 mx-auto mb-2 w-[50%]">
                <SourceSelector value={selectedSources} onChange={setSelectedSources} />
            </div>
            {user.cycleCountManager && (
                <SkuCycleCountForm onSuccess={handleCreateSuccess} closeModal={handleCloseModal} open={modalOpen} />
            )}
            <Dialog open={cancelModalOpen} onClose={handleCloseCancelModal}>
                <DialogTitle>Cancel Cycle Count Request</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to cancel this cycle count request?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCancelModal} color="primary">
                        No, Keep It
                    </Button>
                    <Button onClick={handleCancel} color="secondary">
                        Yes, Cancel Request
                    </Button>
                </DialogActions>
            </Dialog>
            <div className="relative">
                <DataTable
                    columns={columns}
                    data={rows}
                    editable={false}
                    tableContainerMaxHeight={800}
                    expandable
                    toolbarActions={toolbarActions}
                    ExpansionComponent={<SkuCycleCountExpansion setNotification={setNotification} />}
                />
                {isLoading && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255,255,255,0.6)',
                            zIndex: 2,
                        }}
                    >
                        <CircularProgress />
                    </div>
                )}
            </div>
        </>
    );
}

export { SkuCycleCountTab };
